import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { authAPI, clearGuestToken, setSessionExpiredHandler, setIsLoggingOut } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMessagePolling } from '../hooks/useMessagePolling';
import api from '../services/api';

interface User {
  id: number;
  google_id: string;
  email: string;
  name: string;
  picture?: string;
  role: 'user' | 'admin' | 'supervisor' | 'limited_admin';
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isSupervisor: boolean;
  isLimitedAdmin: boolean;
  isGuestAccess: boolean;
  guestToken: string | null;
  login: (provider?: 'google' | 'microsoft') => void;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processMessages } = useMessagePolling();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGuestAccess, setIsGuestAccess] = useState(false);
  const [guestToken, setGuestToken] = useState<string | null>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isLoggingOutRef = useRef(false);

  // For authentication that requires login (admin pages, etc)
  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  const isLimitedAdmin = user?.role === 'limited_admin';
  // For general site access (viewing/taking quizzes)
  const hasAccess = !!user || isGuestAccess;

  const checkAuthStatus = async () => {
    try {
      const response = await authAPI.checkStatus();
      // Process any messages returned from initial status check
      if (response.data.messages && response.data.messages.length > 0) {
        await processMessages(response.data.messages);
      }
      
      if (response.data.user) {
        setUser(response.data.user);
        setIsGuestAccess(false);
      } else if (response.data.isGuestAccess) {
        setUser(null);
        setIsGuestAccess(true);
        setGuestToken(response.data.guestToken || null);
      } else {
        setUser(null);
        setIsGuestAccess(false);
        setGuestToken(null);
      }
    } catch (error) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error checking auth status:', error);
      }
      setUser(null);
      setIsGuestAccess(false);
      setGuestToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (provider: 'google' | 'microsoft' = 'google') => {
    const loginUrl = provider === 'microsoft'
      ? authAPI.getMicrosoftLoginURL()
      : authAPI.getGoogleLoginURL();
    window.location.href = loginUrl;
  };

  const logout = async () => {
    if (isLoggingOutRef.current) {
      return; // Prevent multiple simultaneous logouts
    }
    
    isLoggingOutRef.current = true;
    
    try {
      // Set logout flag BEFORE clearing intervals to prevent session expired handler
      setIsLoggingOut(true);
      
      // Clear interval synchronously
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      
      // Clear state immediately (before API call)
      setUser(null);
      setIsGuestAccess(false);
      setGuestToken(null);
      
      // Make logout API call (401s are expected and will be ignored)
      try {
        await authAPI.logout();
      } catch (error) {
        // Ignore errors during logout - 401s are expected
        if (process.env.NODE_ENV === 'development') {
          console.log('Logout API call completed (errors are expected during logout)');
        }
      }
      
      // Reset logout flag after a short delay to allow any pending requests to complete
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 500);
      
      // Navigate to home page after logout
      navigate('/');
    } catch (error) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error logging out:', error);
      }
      setIsLoggingOut(false);
    } finally {
      isLoggingOutRef.current = false;
    }
  };

  // Handle session expiration (called by API interceptor)
  const handleSessionExpired = useCallback(async (reason: string) => {
    // Don't handle session expiration if user is already logging out intentionally
    if (isLoggingOutRef.current) {
      return;
    }
    
    console.warn('🔒 Session expired, forcing logout:', reason);
    
    // Clear interval
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    setGuestToken(null);
    
    // Show a clear message about what happened
    const message = reason === 'unauthorized' 
      ? 'You do not have permission to access this resource.'
      : 'Your session has expired. Please login again.';
    
    // Navigate to unauthorized page with reason
    navigate(`/unauthorized?reason=${reason}&message=${encodeURIComponent(message)}`);
  }, [navigate]);

  // Set up session keep-alive for authenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear any existing interval if user is not authenticated
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      return;
    }

    console.log('✅ Setting up session management for authenticated user');

    // Keep-alive ping every 2 minutes (also checks for messages)
    keepAliveIntervalRef.current = setInterval(async () => {
      try {
        const response = await api.post('/api/auth/keep-alive');
        console.log('💓 Session keep-alive ping sent');
        // Process any messages returned from keep-alive
        if (response.data.messages && response.data.messages.length > 0) {
          await processMessages(response.data.messages);
        }
      } catch (error) {
        console.error('❌ Keep-alive failed:', error);
        // The API interceptor will handle this and trigger logout
      }
    }, 2 * 60 * 1000); // Every 2 minutes

    return () => {
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, handleSessionExpired]);

  // Register the session expired handler with the API interceptor
  useEffect(() => {
    setSessionExpiredHandler(handleSessionExpired);
    
    return () => {
      setSessionExpiredHandler(() => {});
    };
  }, [handleSessionExpired]);

  // Check auth status on mount and when URL changes
  useEffect(() => {
    checkAuthStatus();
    
    // Listen for URL changes (in case guest token is added)
    const handleLocationChange = () => {
      // Re-check auth status if URL changes
      checkAuthStatus();
    };

    window.addEventListener('popstate', handleLocationChange);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  // Validate session on route changes (for protected routes)
  useEffect(() => {
    // Skip validation if not authenticated or still loading
    if (!isAuthenticated || isLoading) return;
    
    // Only validate for admin/protected routes
    const protectedPaths = ['/admin', '/quiz-responses', '/reports', '/user-management'];
    const isProtectedRoute = protectedPaths.some(path => location.pathname.startsWith(path));
    
    if (isProtectedRoute) {
      // Quick session validation when navigating to protected routes
      authAPI.checkStatus()
        .then(response => {
          if (!response.data.user) {
            console.warn('⚠️ Session invalid on protected route, forcing logout');
            handleSessionExpired('session_expired');
          }
        })
        .catch(error => {
          console.error('❌ Session validation failed on route change:', error);
          // API interceptor will handle this
        });
    }
  }, [location.pathname, isAuthenticated, isLoading, handleSessionExpired]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isAdmin,
    isSupervisor,
    isLimitedAdmin,
    isGuestAccess,
    guestToken,
    login,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 